import React, { createContext, ReactNode, useCallback, useContext, useMemo, useRef } from 'react';

export type AnyFunction = (...args: any[]) => any;

export type PrecloseHandlerContextType = {
  registerPrecloseHandler(handler: AnyFunction): () => void;
  registerSubmitHandler(handler: AnyFunction): void;
  clearHandlers(): void;
  submit(): void;
};

export const PrecloseHandlerContext = createContext<PrecloseHandlerContextType>({
  registerPrecloseHandler: () => () => {
    //
  },
  registerSubmitHandler: () => {
    //
  },
  clearHandlers: () => {
    //
  },
  submit: () => {
    //
  },
});

export function PrecloseHandlerProvider({ children }: { children: ReactNode | undefined }) {
  const precloseHandlers = useRef([] as AnyFunction[]);
  const submitHandler = useRef<null | AnyFunction>(null);

  const registerPrecloseHandler = useCallback((handler: AnyFunction) => {
    precloseHandlers.current.push(handler);

    return () => {
      precloseHandlers.current = precloseHandlers.current.filter((each) => each !== handler);
    };
  }, []);

  const registerSubmitHandler = useCallback((handler: AnyFunction) => {
    submitHandler.current = handler;
  }, []);

  const clearHandlers = useCallback(() => {
    precloseHandlers.current = [];
    submitHandler.current = null;
  }, []);

  const submit = useCallback(async () => {
    await Promise.all(precloseHandlers.current.map((handler) => handler()));
    submitHandler.current?.();
    return clearHandlers();
  }, [clearHandlers]);

  const value = useMemo(
    () => ({
      registerPrecloseHandler,
      registerSubmitHandler,
      clearHandlers,
      submit,
    }),
    [registerPrecloseHandler, registerSubmitHandler, clearHandlers, submit],
  );

  return <PrecloseHandlerContext.Provider value={value}>{children}</PrecloseHandlerContext.Provider>;
}

export function usePrecloseHandlers() {
  return useContext(PrecloseHandlerContext);
}
